import { isPlatformServer } from '@angular/common';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import {
  CreateGalleryBody,
  CreateGalleryFromPresetBody,
  CreateGalleryFromScratchBody,
  DisplayUser,
  DisplayUserEmail,
  DuplicateGalleryBody,
  GalleryDto,
  GalleryPublicAccessLevel,
  GalleryTenantAccessLevel,
  TenantDto,
  UpdateGalleryBodyDto,
} from '@nusign/picflow-backend-types';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  combineLatest,
  concat,
  distinctUntilKeyChanged,
  filter,
  firstValueFrom,
  lastValueFrom,
  map,
  merge,
  of,
  shareReplay,
  skip,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';
import { NonPaymentDialogComponent } from '../account/dialogs/non-payment-dialog/non-payment-dialog.component';
import { ScreenSizes } from '../classes/screen-sizes.class';
import { defaultGalleryIncludes, extraParamsForCreateGalleryFromPreset, standardMemoryCacheExpiry } from '../consts';
import { GalleryAccessLevel } from '../lib/access-level';
import { addGalleryPassword } from '../lib/add-gallery-password';
import { addIdPrefix } from '../lib/add-id-prefix';
import { copy } from '../lib/copy';
import { fixPermissions } from '../lib/fix-permissions';
import { getAllRouteParams } from '../lib/get-all-route-params';
import { GetSubGalleryFields, getPresetChanges } from '../lib/new-gallery';
import { noneUndefined } from '../lib/none-undefined';
import { randomString } from '../lib/random-string';
import { sleep } from '../lib/sleep';
import { unique } from '../lib/unique';
import { IGNORE_AUTH_ERRORS, IGNORE_MAINTENANCE } from '../network-interceptor';
import { PICFLOW_ANALYTICS } from '../picflow-analytics';
import { AppConfigService } from '../providers/config.service';
import { sortGalleriesAndFolders } from '../shared/pipes/galleries-sort-by.pipe';
import { GalleryTeaserUrlPipe } from '../shared/pipes/gallery-teaser-url.pipe';
import { AssetPreGenerateService } from './asset-pre-generate.service';
import { BrowserService } from './browser.service';
import { ChecklistService } from './checklist.service';
import { DialogService } from './dialog.service';
import { GalleriesCommonService } from './galleries-common.service';
import { GalleryDisplayPreferencesService } from './gallery-display-preferences.service';
import { GalleryPasswordService } from './gallery-password.service';
import { GalleryPresetService } from './gallery-preset.service';
import { NonPaymentService } from './non-payment.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { OrganizationService } from './organization.service';
import { PaginationService } from './pagination.service';
import { SectionService } from './section.service';
import { SentryService } from './sentry.service';
import { SharedWithMeGalleryService } from './shared-with-me-gallery.service';
import { SocketService } from './socket.service';
import { StateService } from './state.service';
import { StorageService } from './storage.service';
import { TenantService } from './tenant.service';
import { UploadFromUrlService } from './upload-from-url.service';
import { UserGroupService } from './user-group.service';
import { ViewGalleryHierarchyService } from './view-gallery-hierarchy.service';

@Injectable({
  providedIn: 'root',
})
export class GalleryService {
  private http = inject(HttpClient);
  private config = inject(AppConfigService);
  private loader = inject(ObservableDataLoaderService);
  private state = inject(StateService);
  private sectionService = inject(SectionService);
  private storage = inject(StorageService);
  private tenantService = inject(TenantService);
  private organizationService = inject(OrganizationService);
  private dialog = inject(DialogService);
  private checklistService = inject(ChecklistService);
  private socketService = inject(SocketService);
  private nonPaymentService = inject(NonPaymentService);
  private sentryService = inject(SentryService);
  private galleryHierarchyService = inject(ViewGalleryHierarchyService);
  private platformId = inject<string>(PLATFORM_ID);
  private galleriesCommonService = inject(GalleriesCommonService);
  private sharedWithMeGalleryService = inject(SharedWithMeGalleryService);
  private browserService = inject(BrowserService);
  private uploadFromUrlService = inject(UploadFromUrlService); // just here to load the service
  private galleryPresetService = inject(GalleryPresetService);
  private galleryPasswordService = inject(GalleryPasswordService);
  private teaserUrl = inject(GalleryTeaserUrlPipe);
  private assetPreGenerateService = inject(AssetPreGenerateService);
  private userGroupService = inject(UserGroupService);
  private galleryDisplayPreferencesService = inject(GalleryDisplayPreferencesService);
  private paginationService = inject(PaginationService);

  private viewedGalleries: string[] = [];
  public ongoingUpdates: Set<string> = new Set();

  public galleryUpdateTimeMap: Map<string, number> = new Map();

  private optimisticUpdates = new BehaviorSubject<{
    id: string;
    params: UpdateGalleryBodyDto & { is_favourite?: boolean };
  }>(undefined);

  public onGalleryCreated$ = new BehaviorSubject<GalleryDto>(undefined);
  public onGalleryUpdated$ = new BehaviorSubject<GalleryDto>(undefined);
  public onGalleryDeleted$ = new BehaviorSubject<GalleryDto>(undefined);

  private galleryCreatedWatcher = this.onGalleryCreated$.subscribe(gallery => {
    if (!gallery) return;
    this.loader.updateFuzzy<GalleryDto[]>(`/galleries?`, galleries => {
      if (!galleries) return;
      const tenant = this.state.tenants?.find(t => t.id === gallery.tenant) || gallery.tenant;
      if (Array.isArray(tenant)) {
        console.error('Tenant is an array galleryCreatedWatcher', tenant);
        // eslint-disable-next-line no-debugger
        debugger;
      }
      gallery.tenant = tenant;
      this.galleriesCommonService.fixGallery(gallery);
      galleries.push(gallery);
      return unique(galleries, 'id');
    });
  });

  private galleryUpdatedWatcher = this.onGalleryUpdated$.subscribe(gallery => {
    if (!gallery) return;

    // Gallery lists
    this.loader.updateFuzzy<GalleryDto[]>(`/galleries?`, galleries => {
      if (!galleries) return;

      const index = galleries.findIndex(g => g.id === gallery.id);
      gallery = this.FixGalleryForUpdate(gallery, galleries[index]);

      if (index === -1) {
        // We can push galleries to every list, even if that list isn't supposed to have the gallery because the list should be filtered after this
        // For example: a list of galleries for one section filters out galleries that are not in that section
        // galleries.push(gallery);
        return galleries;
      }

      // Collecting values from the old gallery, which are not sent in the websocket update
      const accessLevel = galleries[index].access_level;
      const is_favourite = galleries[index].is_favourite;

      // Updating the gallery
      galleries[index] = gallery;

      // Restoring the values that were not sent in the websocket
      if (accessLevel) gallery.access_level = accessLevel;
      if (is_favourite) gallery.is_favourite = is_favourite;
      return galleries;
    });

    // Individual gallery
    this.loader.updateFuzzy<GalleryDto>(`/galleries/`, oldValue => {
      if (oldValue?.id !== gallery.id) return oldValue;
      return this.FixGalleryForUpdate(gallery, oldValue);
    });
  });

  private galleryOptimisticUpdateWatcher = this.optimisticUpdates.subscribe(data => {
    if (!data) return;
    const { id, params } = data;
    this.loader.updateFuzzy<GalleryDto[]>(`/galleries?`, galleries => {
      if (!galleries) return;
      const index = galleries.findIndex(g => g.id === id);
      if (index === -1) return;
      galleries[index] = { ...galleries[index], ...noneUndefined(params) };
      return galleries;
    });

    this.loader.updateFuzzy<GalleryDto>(`/galleries/`, oldValue => {
      if (oldValue?.id !== id) return;
      return { ...oldValue, ...noneUndefined(params) };
    });

    if (data.params.teaser) {
      [
        this.teaserUrl.transform(data.params as GalleryDto, 600), // default size for gallery teaser
        this.teaserUrl.transform(data.params as GalleryDto, 1), // smallest size for gallery teaser in folder
      ].forEach(url => {
        this.assetPreGenerateService.preloadAsset(url);
      });
    }
  });

  private FixGalleryForUpdate(gallery: GalleryDto, oldGallery?: GalleryDto) {
    if (gallery.section && !gallery.parent_gallery_id) {
      const parentGallery = this.galleryHierarchyService.getParent(gallery.id);
      if (parentGallery) gallery.parent_gallery_id = parentGallery?.galleryId;
      else {
        console.warn('Could not find parent gallery for section', gallery.id);
      }
    }
    if (typeof gallery.tenant === 'string') {
      const tenant = oldGallery?.tenant || this.state.tenants?.find(t => t.id === gallery.tenant) || gallery.tenant;
      if (Array.isArray(tenant)) {
        console.error('Tenant is an array FixGalleryForUpdate', tenant);
        // eslint-disable-next-line no-debugger
        debugger;
      }
      gallery.tenant = tenant;
    }

    gallery.tenant_product_name = gallery.tenant_product_name || oldGallery?.tenant_product_name;

    if (oldGallery && !gallery.section && oldGallery.section) {
      delete oldGallery.parent_gallery_id;
      delete oldGallery.section;
    }
    this.galleriesCommonService.fixGallery(gallery);
    if (!gallery.permissions) {
      gallery.permissions = oldGallery?.permissions;
    }
    if (!gallery.public_access_effective && (!oldGallery || gallery.public_access === oldGallery.public_access)) {
      gallery.public_access_effective = oldGallery?.public_access_effective;
    }
    if (!gallery.tenant_access_effective && (!oldGallery || gallery.tenant_access === oldGallery.tenant_access)) {
      gallery.tenant_access_effective = oldGallery?.tenant_access_effective;
    }
    return gallery;
  }

  private galleryDeletedWatcher = this.onGalleryDeleted$.subscribe(gallery => {
    if (!gallery) return;
    this.loader.updateFuzzy<GalleryDto[]>(`/galleries?`, galleries => {
      if (!galleries) return;
      const isInGalleriesList = galleries.some(g => g.id === gallery.id);
      if (!isInGalleriesList) return;
      return galleries.filter(g => g.id !== gallery.id);
    });
  });

  private analytics = inject(PICFLOW_ANALYTICS);

  constructor() {
    if (isPlatformServer(this.platformId)) return;
    this.viewedGalleries = this.storage.get('viewed_galleries') || [];

    this.socketService.onSocketEvent.subscribe(event => {
      if (!event?.payload) return;
      const gallery = event.payload?.gallery as GalleryDto;

      if (!gallery) return;
      const lastLocalUpdate = this.galleryUpdateTimeMap.get(gallery.id);
      if (lastLocalUpdate && lastLocalUpdate > gallery.updated_at * 1000) return;

      if (event.name === 'gallery.deleted') {
        this.onGalleryDeleted$.next(gallery);
      }

      // if (event.name === 'gallery.created') {
      //   this.getGallery(gallery.id)
      //     .pipe(
      //       take(1),
      //       catchError(() => of(gallery))
      //     )
      //     .subscribe(gallery => {
      //       this.onGalleryCreated$.next(gallery);
      //     });
      // }

      if (event.name === 'gallery.updated') {
        this.onGalleryUpdated$.next(gallery);
      }
    });
  }

  public getAllGalleries(): Observable<
    Omit<GalleryDto, 'permissions' | 'public_access_effective' | 'tenant_access_effective'>[]
  > {
    if (this.state.sharedWithMeActiveTenant$.value) {
      return this.getSharedWithMeGalleries();
    }

    const url = `${this.config.apiEndpoint}/galleries?${defaultGalleryIncludes}&section=none&context=dashboard`;
    return this.loader
      .get(
        url,
        () => this.http.get<{ data: GalleryDto[] }>(url).pipe(map(resp => resp.data)),
        standardMemoryCacheExpiry,
        false
      )
      .pipe(
        switchMap(galleries => this.filterAndHandleNewGalleries(galleries)),
        map(galleries => galleries.filter(g => !g.section))
      );
  }

  public getAllGalleriesIncludingSubgalleries(): Observable<
    Omit<GalleryDto, 'permissions' | 'public_access_effective' | 'tenant_access_effective'>[]
  > {
    if (this.state.sharedWithMeActiveTenant$.value) {
      return this.getSharedWithMeGalleriesIncludingSubGalleries();
    }
    const url = `${this.config.apiEndpoint}/galleries?${defaultGalleryIncludes}&context=dashboard`;
    return this.loader
      .get(
        url,
        () => this.http.get<{ data: GalleryDto[] }>(url).pipe(map(resp => resp.data)),
        standardMemoryCacheExpiry,
        false
      )
      .pipe(switchMap(galleries => this.filterAndHandleNewGalleries(galleries)));
  }

  public getGroupGalleries(
    groupId: string
  ): Observable<Omit<GalleryDto, 'permissions' | 'public_access_effective' | 'tenant_access_effective'>[]> {
    return combineLatest([this.getAllGalleries(), this.userGroupService.getGroupGalleries(groupId)]).pipe(
      map(([galleries, groupGalleries]) => {
        return galleries.filter(g => groupGalleries.some(gg => gg.gallery === g.id));
      })
    );
  }

  public getSectionGalleries(
    sectionId: string,
    galleryId: string
  ): Observable<Omit<GalleryDto, 'permissions' | 'public_access_effective' | 'tenant_access_effective'>[]> {
    const url = addGalleryPassword(
      `${this.config.apiEndpoint}/galleries?${defaultGalleryIncludes}&gallery=${galleryId}&section=${sectionId}`,
      this.galleryPasswordService.getPassword(galleryId)
    );
    return this.loader
      .get(
        url,
        () => this.http.get<{ data: GalleryDto[] }>(url).pipe(map(resp => resp.data)),
        standardMemoryCacheExpiry,
        false
      )
      .pipe(
        map(galleries => galleries.filter(g => g.section === sectionId)),
        tap(galleries => {
          this.galleryHierarchyService.add(
            galleryId,
            galleries.map(g => g.id),
            galleries.map(g => g.path)
          );
        }),
        switchMap(galleries => this.filterAndHandleNewGalleries(galleries))
      );
  }

  private sharedWithMeGalleries$: Observable<
    Omit<
      GalleryDto & { access_level: GalleryAccessLevel },
      'permissions' | 'public_access_effective' | 'tenant_access_effective'
    >[]
  > = this.state.sharedWithMeActiveTenant$.pipe(
    switchMap(() => {
      return concat(
        of(undefined),
        this.sharedWithMeGalleryService.getSharedWithMeGalleriesRaw().pipe(
          map(galleries => copy(galleries)),
          switchMap(galleries => this.filterAndHandleNewGalleries(galleries)),
          map(galleries => {
            const activeTenant = this.state.activeTenant$?.value;
            const activeSharedWithMeTenant = this.state.sharedWithMeActiveTenant$.value;
            const tenantId = activeSharedWithMeTenant?.id || activeTenant?.id;
            if (!tenantId)
              return galleries as (GalleryDto & {
                access_level: GalleryAccessLevel;
              })[];
            return galleries.filter(g => {
              if (!g.tenant) return false;
              const galleryTenantId = (g.tenant?.['id'] || g.tenant) as string;
              return galleryTenantId === tenantId;
            }) as (GalleryDto & {
              access_level: GalleryAccessLevel;
            })[];
          })
        )
      );
    }),
    shareReplay(1)
  );

  private sharedWithMeGalleriesIncludingSubGalleries$ = this.sharedWithMeGalleries$.pipe(
    switchMap(galleries => {
      return combineLatest(
        galleries.map(gallery =>
          this.getSubGalleries(gallery.id).pipe(
            map(subGalleries => [gallery, ...subGalleries.map(g => ({ ...g, access_level: gallery.access_level }))])
          )
        )
      ).pipe(map(galleries => galleries.flat()));
    })
  );

  public getSharedWithMeGalleries() {
    if (!this.state.user)
      return of(
        [] as (GalleryDto & {
          access_level: GalleryAccessLevel;
        })[]
      );

    return this.sharedWithMeGalleries$.pipe(filter(Boolean));
  }

  public getSharedWithMeGalleriesIncludingSubGalleries() {
    if (!this.state.user)
      return of(
        [] as (GalleryDto & {
          access_level: GalleryAccessLevel;
        })[]
      );

    return this.sharedWithMeGalleriesIncludingSubGalleries$.pipe(filter(Boolean));
  }

  public getSubGalleries(
    galleryId: string
  ): Observable<
    Omit<
      GalleryDto & { access_level: GalleryAccessLevel },
      'permissions' | 'public_access_effective' | 'tenant_access_effective'
    >[]
  > {
    return this.sectionService.getSections(galleryId).pipe(
      switchMap(sections => {
        const gallerySections = sections.filter(section => section.object === 'gallery');
        if (!gallerySections.length) return of([]);
        return combineLatest(gallerySections.map(section => this.getSectionGalleries(section.id, galleryId))).pipe(
          map(galleries => galleries.flat())
        );
      })
    );
  }

  public getGalleriesPaginated(
    folder: string,
    next: BehaviorSubject<void>,
    limit = ScreenSizes.screenSize() === 'mobile' ? 12 : 24
  ): Observable<{ galleries: GalleryDto[]; has_more_next: boolean }> {
    if (!folder) folder = 'none';
    return this.state.activeTenant$.pipe(
      filter(Boolean),
      distinctUntilKeyChanged('id'),
      switchMap(activeTenant => {
        if (!activeTenant) return of(undefined);

        let allGalleries: GalleryDto[] = [];
        return this.galleryDisplayPreferencesService.displayPreferences$.pipe(
          filter(Boolean),
          switchMap(prefs => {
            allGalleries = [];
            return next.pipe(
              switchMap(() => {
                const lastGallery = allGalleries[allGalleries.length - 1];
                const pageAfter = this.paginationService.encodePageInfo(lastGallery, [prefs.sorting]);
                const url = `${this.config.apiEndpoint}/galleries?${defaultGalleryIncludes}&section=none${this.config.isTenantDomain ? `` : '&context=dashboard'}${pageAfter ? `&page_after=${pageAfter}` : ''}${`&limit=${limit}`}&folder=${folder}${prefs.sorting ? `&sort[]=${prefs.sorting}` : ''}`;
                return this.http.get<{ data: GalleryDto[]; has_more_next: boolean }>(url).pipe(
                  switchMap(resp => {
                    return this.filterAndHandleNewGalleries(resp.data).pipe(
                      map(galleries => {
                        allGalleries.push(...galleries);
                        allGalleries = unique(allGalleries, 'id', true);
                        return resp.has_more_next;
                      })
                    );
                  })
                );
              })
            );
          }),
          switchMap(has_more_next => {
            // Listen for gallery updates and deletions
            return merge(
              this.onGalleryUpdated$.pipe(
                skip(1),
                map(gallery => ({ event: 'updated', gallery }))
              ),
              this.onGalleryDeleted$.pipe(
                skip(1),
                map(gallery => ({ event: 'deleted', gallery }))
              ),
              this.onGalleryCreated$.pipe(
                skip(1),
                map(gallery => ({ event: 'created', gallery }))
              ),
              of({ event: 'initial', gallery: undefined as GalleryDto })
            ).pipe(
              switchMap(data => {
                if (data.event === 'initial') return of({ galleries: allGalleries, has_more_next });
                const updatedGallery = data.event === 'updated' ? data.gallery : undefined;
                const deletedGallery = data.event === 'deleted' ? data.gallery : undefined;
                const createdGallery = data.event === 'created' ? data.gallery : undefined;

                return this.galleryDisplayPreferencesService.displayPreferences$.pipe(
                  filter(Boolean),
                  take(1),
                  map(prefs => {
                    if (updatedGallery) {
                      const galleryMovedToSection = !!updatedGallery.section;
                      const galleryMovedToOtherFolder = (updatedGallery.folder || 'none') !== folder;
                      if (galleryMovedToSection || galleryMovedToOtherFolder) {
                        // If the gallery was moved to another section or folder, we need to remove it from the current list
                        allGalleries = allGalleries.filter(g => g.id !== updatedGallery.id);
                      } else {
                        const index = allGalleries.findIndex(f => f.id === updatedGallery.id);
                        if (index > -1) {
                          allGalleries[index] = { ...allGalleries[index], ...updatedGallery };
                        }
                      }
                    }
                    if (deletedGallery) {
                      allGalleries = allGalleries.filter(f => f.id !== deletedGallery.id);
                    }
                    if (createdGallery && (createdGallery.folder || 'none') === folder) {
                      this.galleriesCommonService.fixGallery(createdGallery);
                      allGalleries.push(createdGallery);
                    }

                    // Re-sort the folders based on the current sorting preference
                    const sorting = prefs.sorting;
                    allGalleries = sortGalleriesAndFolders(allGalleries, false, sorting, false);
                    return {
                      galleries: allGalleries,
                      has_more_next,
                    };
                  })
                );
              })
            );
          })
        );
      })
    );
  }

  public getGalleriesPaginatedAll(folder: string): Observable<GalleryDto[]> {
    const next = new BehaviorSubject<void>(undefined);
    return this.getGalleriesPaginated(folder, next, 100).pipe(
      map(resp => {
        if (!resp.has_more_next) {
          return resp.galleries;
        }
        next.next();
        return undefined;
      }),
      filter(Boolean)
    );
  }

  public getGallery(
    galleryId: string,
    options: { ignore503Errors?: boolean; noCache?: boolean; effectiveAccessLevels?: boolean } = {}
  ): Observable<GalleryDto> {
    const url = addGalleryPassword(
      `${this.config.apiEndpoint}/galleries/${galleryId}?${defaultGalleryIncludes}&expand[]=tenant${options.effectiveAccessLevels ? '&include[]=effective_access' : ''}`,
      this.galleryPasswordService.getPassword(galleryId)
    );

    return this.loader.get(
      `${url}${options.noCache ? Date.now().toString() : ''}`,
      () => {
        return this.getGalleryFromUrl(
          url,
          new HttpContext().set(IGNORE_AUTH_ERRORS, true).set(IGNORE_MAINTENANCE, options.ignore503Errors)
        );
      },
      standardMemoryCacheExpiry,
      false
    );
  }

  public getGalleryPermissions(gallery: GalleryDto) {
    if (!gallery) return of(undefined);
    if (gallery.permissions) return of(fixPermissions(gallery.permissions));
    return this.getGallery(gallery.id, { noCache: true }).pipe(
      take(1),
      map(gallery => fixPermissions(gallery.permissions))
    );
  }

  public getEffectivePublicAccessLevel(gallery: GalleryDto): Observable<GalleryPublicAccessLevel> {
    if (gallery.public_access) return of(gallery.public_access);
    if (gallery.public_access_effective) return of(gallery.public_access_effective);

    return this.getGallery(gallery.id, { effectiveAccessLevels: true }).pipe(
      map(gallery => gallery.public_access_effective)
    );
  }
  public getEffectiveTenantAccessLevel(gallery: GalleryDto): Observable<GalleryTenantAccessLevel> {
    if (gallery.tenant_access) return of(gallery.tenant_access);
    if (gallery.tenant_access_effective) return of(gallery.tenant_access_effective);

    return this.getGallery(gallery.id, { effectiveAccessLevels: true }).pipe(
      map(gallery => gallery.tenant_access_effective)
    );
  }

  private getGalleryByPath(path: string, options?: { ignore503Errors?: boolean }): Observable<GalleryDto> {
    const url = addGalleryPassword(
      `${this.config.apiEndpoint}/galleries/path/${path}?${defaultGalleryIncludes}&expand[]=tenant`,
      this.galleryPasswordService.getPassword(path)
    );
    return this.loader
      .get(
        url,
        () =>
          this.getGalleryFromUrl(
            url,
            new HttpContext().set(IGNORE_AUTH_ERRORS, true).set(IGNORE_MAINTENANCE, options?.ignore503Errors)
          ),
        standardMemoryCacheExpiry,
        false
      )
      .pipe(
        tap(gallery => {
          const url = addGalleryPassword(
            `${this.config.apiEndpoint}/galleries/${gallery.id}?${defaultGalleryIncludes}&expand[]=tenant`,
            this.galleryPasswordService.getPassword(gallery.id)
          );
          this.loader.get(url, () => of(gallery), standardMemoryCacheExpiry, false);
        })
      );
  }

  private getGalleryFromUrl(url: string, context?: HttpContext): Observable<GalleryDto> {
    return this.http.get<GalleryDto>(url, { context }).pipe(
      switchMap(gallery => this.filterAndHandleNewGalleries([gallery])),
      map(galleries => galleries[0]),
      switchMap(gallery => {
        if (gallery) return of(gallery);
        return throwError(() => new HttpErrorResponse({ status: 404, statusText: 'Not Found', url }));
      })
    );
  }

  // public async testGalleryPerformance() {
  //   const runs = 100;
  //   const delayBetween = 100;
  //   const url = `${this.config.apiEndpoint}/galleries?${defaultGalleryIncludes}&section=none`;
  //   this.http.get<{ data: GalleryDto[] }>(url);

  //   const results = [];

  //   console.log('Starting performance test');

  //   for (let i = 0; i < runs; i++) {
  //     const start = Date.now();
  //     await firstValueFrom(this.http.get<{ data: GalleryDto[] }>(url));
  //     // resp.data.forEach(g => this.updateGallery(g.id, { public_access: 'no_access' }));
  //     const timeTaken = Date.now() - start;
  //     results.push(timeTaken);
  //     await sleep(delayBetween);
  //   }
  //   console.log('Gallery performance test', results);
  //   console.log('Min', Math.min(...results));
  //   console.log('Max', Math.max(...results));
  //   console.log('Median', results.sort((a, b) => a - b)[Math.floor(results.length / 2)]);
  // }

  public getGalleryOwner(galleryId: string, isTeamMember: boolean): Observable<DisplayUserEmail> {
    return this.loader.get(
      `gallery_owner_${galleryId}`,
      () => {
        const url = addGalleryPassword(
          `${this.config.apiEndpoint}/galleries/${galleryId}?expand[]=owner`,
          this.galleryPasswordService.getPassword(galleryId)
        );
        return this.http
          .get<GalleryDto & { owner: DisplayUser }>(url, { context: new HttpContext().set(IGNORE_AUTH_ERRORS, true) })
          .pipe(
            switchMap(gallery => {
              return (isTeamMember ? this.organizationService.getMembers() : of([])).pipe(
                map(members => {
                  return {
                    ...(gallery.owner as DisplayUser),
                    email: members.find(m => m.id === gallery.owner.id)?.email,
                  };
                })
              );
            }),
            catchError(() => of(undefined))
          );
      },
      standardMemoryCacheExpiry,
      false
    );
  }

  public changeGalleryOwner(galleryId: string, userId: string) {
    return this.http
      .patch(`${this.config.apiEndpoint}/galleries/${galleryId}`, <UpdateGalleryBodyDto>{
        transfer_ownership: {
          transferee: userId,
        },
      })
      .pipe(
        tap(() => {
          this.loader.remove(`gallery_owner_${galleryId}`);
        })
      );
  }

  public getTenantProfileGalleriesPaginated(
    next: BehaviorSubject<void>,
    tenant: TenantDto,
    limit = ScreenSizes.screenSize() === 'mobile' ? 12 : 24
  ): Observable<{ galleries: GalleryDto[]; has_more_next: boolean }> {
    let allGalleries: GalleryDto[] = [];

    return next.pipe(
      switchMap(() => {
        const sorting = tenant.public_gallery_sorting || 'position';
        const lastGallery = allGalleries[allGalleries.length - 1];
        const pageAfter = this.paginationService.encodePageInfo(lastGallery, [sorting]);
        const url = `${this.config.apiEndpoint}/tenants/${tenant.id}/galleries?include[]=tenant_product_name&include[]=parent_gallery_id&section=none${pageAfter ? `&page_after=${pageAfter}` : ''}${`&limit=${limit}`}&sort[]=${sorting}`;

        return this.http.get<{ data: GalleryDto[]; has_more_next: boolean }>(url).pipe(
          switchMap(resp => {
            return this.filterAndHandleNewGalleries(resp.data).pipe(
              map(galleries => {
                allGalleries.push(...galleries);
                allGalleries = unique(allGalleries, 'id', true);
                return {
                  galleries: allGalleries,
                  has_more_next: resp.has_more_next,
                };
              })
            );
          })
        );
      })
    );
  }

  public getPublicFolderGalleries(folderId: string): Observable<GalleryDto[]> {
    return this.loader
      .get(
        `galleries_folder_${folderId}`,
        () =>
          this.http
            .get<{ data: GalleryDto[] }>(`${this.config.apiEndpoint}/galleries?&folder=${folderId}`)
            .pipe(map(resp => resp.data)),
        standardMemoryCacheExpiry,
        false
      )
      .pipe(switchMap(galleries => this.filterAndHandleNewGalleries(galleries)));
  }

  public async getPublicGalleryFromSnapshot(
    next: ActivatedRouteSnapshot,
    options: { ignore503Errors?: boolean; noCache?: boolean } = {}
  ) {
    const params = getAllRouteParams(next);
    const idOrPath: string = params.galleryPath || addIdPrefix(params.galleryId, 'gal') || '';

    if (this.galleriesCommonService.galleryPathIsId.has(idOrPath)) {
      return await firstValueFrom(this.getGallery(idOrPath, options));
    }
    try {
      if (!this.config.isTenantDomain) {
        // localhost and showcase
        return await firstValueFrom(this.getGallery(idOrPath, options));
      }
      return await firstValueFrom(this.getGalleryByPath(idOrPath, options));
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      if (err.status !== 404 && err.status !== 403 && err.status !== 500) throw err;
      if (!idOrPath.startsWith('gal_')) throw err;
      // if error returns 404 then we can see if we can find it by id instead
      return await firstValueFrom(this.getGallery(idOrPath, options));
    }
  }

  public async createSubGallery(parentGallery: GalleryDto, parentSectionId?: string) {
    const isBlocked = await firstValueFrom(this.nonPaymentService.isBlocked());
    if (isBlocked) {
      this.dialog.open(NonPaymentDialogComponent);
      return;
    }
    const params: CreateGalleryFromScratchBody = GetSubGalleryFields(parentGallery);
    const section = await firstValueFrom(
      this.sectionService.getOrCreateSection('gallery', parentGallery.id, parentSectionId)
    );
    if (!section) return;
    params.section = section.id;
    const path = randomString(10);
    return this.createGalleryRaw({ path, ...params });
  }

  public async createGalleryFromPreset(params: CreateGalleryFromPresetBody): Promise<GalleryDto> {
    const isBlocked = await firstValueFrom(this.nonPaymentService.isBlocked());
    if (isBlocked) {
      this.dialog.open(NonPaymentDialogComponent);
      return;
    }
    const path = randomString(10);

    const standardPresets = await firstValueFrom(this.galleryPresetService.getStandardGalleryPresets());
    if (standardPresets.find(p => p.id === params.preset)) {
      return this.createGalleryFromStandardPreset(params, path);
    }

    return this.createGalleryRaw({ path, ...params, ...extraParamsForCreateGalleryFromPreset });
  }

  private async createGalleryFromStandardPreset(
    params: CreateGalleryFromPresetBody,
    path: string
  ): Promise<GalleryDto> {
    const standardPresets = await firstValueFrom(this.galleryPresetService.getStandardGalleryPresets());
    const preset = standardPresets.find(p => p.id === params.preset);
    const presetParams = getPresetChanges(preset);
    delete params.preset; // The backend would try to find this in the presets table

    return this.createGalleryRaw({
      path,
      ...presetParams,
      ...params,
      ...extraParamsForCreateGalleryFromPreset,
    });
  }

  public async duplicateGallery(
    galleryId: string,
    settings: {
      skipDuplicateAssets?: boolean;
      skipDuplicateGalleries?: boolean;
    } = {}
  ): Promise<GalleryDto> {
    try {
      const params: DuplicateGalleryBody = {
        copy_from: galleryId,
        disable_copy_assets: settings.skipDuplicateAssets,
        disable_copy_galleries: settings.skipDuplicateGalleries,
      };
      const responseGallery = <GalleryDto>(
        await lastValueFrom(this.http.post(this.config.apiEndpoint + '/galleries', params))
      );

      await sleep(3000); // spicedb delay. TODO: check if this is still needed

      this.getGallery(responseGallery.id)
        .pipe(
          take(1),
          catchError(() => of())
        )
        .subscribe(gallery => {
          this.onGalleryCreated$.next(gallery);
        });

      this.analytics.track('gallery_duplicated');
      return responseGallery;
    } catch (err) {
      this.handleCreateGalleryError(err);
    }
  }

  async createGalleryRaw(params: CreateGalleryBody) {
    const isBlocked = await firstValueFrom(this.nonPaymentService.isBlocked());
    if (isBlocked) {
      this.dialog.open(NonPaymentDialogComponent);
      return;
    }
    try {
      const gallery = await lastValueFrom(this.http.post<GalleryDto>(this.config.apiEndpoint + '/galleries', params));
      this.analytics.track('gallery_created', false, {
        type: params['section'] ? 'sub-gallery' : 'main-gallery',
        id: gallery.id,
      });

      this.getGallery(gallery.id)
        .pipe(
          take(1),
          catchError(() => of(gallery))
        )
        .subscribe(gallery => {
          this.onGalleryCreated$.next(gallery);
        });
      return gallery;
    } catch (err) {
      this.handleCreateGalleryError(err);
    }
  }

  private async handleCreateGalleryError(err: Error) {
    if (!(err instanceof HttpErrorResponse)) throw err;
    if (err.status !== 400) throw err;
    // if (err.error.code === 'upgrade_plan_required') {
    //   // plan must have been updated in the background
    //   this.dialog.open(GalleryLimitReachedDialogComponent, {});
    //   return;
    // }
    console.error('Backend error, couldnt create gallery', err);
    await sleep(0);
    // eslint-disable-next-line no-debugger
    debugger;

    this.sentryService.captureException('create_gallery_error');
    await sleep(100);

    this.browserService.reload(true);
  }

  public optimisticUpdateGallery(galleryId: string, params: UpdateGalleryBodyDto & { is_favourite?: boolean }) {
    this.galleryUpdateTimeMap.set(galleryId, Date.now());
    this.optimisticUpdates.next({ id: galleryId, params });
  }

  public async updateGallery(galleryId: string, params: UpdateGalleryBodyDto): Promise<GalleryDto> {
    this.ongoingUpdates.add(galleryId);

    const dontDoOptimisticUpdate = ['section'].some(key => params[key]);
    if (!dontDoOptimisticUpdate) {
      this.galleryUpdateTimeMap.set(galleryId, Date.now());
      this.optimisticUpdates.next({ id: galleryId, params });
    }

    try {
      const url = addGalleryPassword(
        `${this.config.apiEndpoint}/galleries/${galleryId}`,
        this.galleryPasswordService.getPassword(galleryId)
      );
      const [resp] = await Promise.all([
        lastValueFrom(
          this.http.patch<GalleryDto>(url, params).pipe(
            tap(gallery => {
              const lastLocalUpdate = this.galleryUpdateTimeMap.get(gallery.id);
              if (lastLocalUpdate && lastLocalUpdate > Date.now()) return;
              this.onGalleryUpdated$.next(gallery);
            })
          )
        ),
        this.updateSubGalleries(galleryId, params),
      ]);

      if (params.cover?.uuid) {
        this.checklistService.markItemAsDone('setCover').subscribe();
      }
      this.ongoingUpdates.delete(galleryId);
      return resp;
    } catch (err) {
      this.ongoingUpdates.delete(galleryId);
      throw err;
    }
  }

  public async refreshWatermark(galleryId): Promise<GalleryDto> {
    const params: UpdateGalleryBodyDto = { refresh_watermark: true };
    return lastValueFrom(this.http.patch<GalleryDto>(this.config.apiEndpoint + '/galleries/' + galleryId, params));
  }

  public async deleteGallery(gallery: GalleryDto) {
    await lastValueFrom(this.http.delete(this.config.apiEndpoint + '/galleries/' + gallery.id));
    this.onGalleryDeleted$.next(gallery);
    this.tenantService.refreshTenant();
  }

  public async favoriteGallery(gallery: GalleryDto) {
    this.optimisticUpdates.next({ id: gallery.id, params: { is_favourite: true } });
    await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/galleries/${gallery.id}/favourite`, {}));
    this.onGalleryUpdated$.next({ ...gallery, is_favourite: true });
  }

  public async unFavoriteGallery(gallery: GalleryDto) {
    this.optimisticUpdates.next({ id: gallery.id, params: { is_favourite: false } });
    await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/galleries/${gallery.id}/favourite`));
    this.onGalleryUpdated$.next({ ...gallery, is_favourite: false });
  }

  public async fakeGalleryView(galleryId: string): Promise<void> {
    this.viewedGalleries.push(galleryId);
    this.storage.set('viewed_galleries', this.viewedGalleries, true);
  }

  public async galleryView(galleryId: string): Promise<void> {
    if (this.viewedGalleries.includes(galleryId)) return;
    if (this.state.impersonating) return;
    const email = this.state.user?.email;
    if (email && ['@nusign.com', '@picflow.com'].find(_email => email.endsWith(_email))) {
      // preventing internal views as counting unless spesifically allowed
      return;
    }
    this.viewedGalleries.push(galleryId);
    this.storage.set('viewed_galleries', this.viewedGalleries, true);
    await lastValueFrom(
      this.http.post(
        addGalleryPassword(
          `${this.config.apiEndpoint}/galleries/${galleryId}/views?gallery=${galleryId}`,
          this.galleryPasswordService.getPassword(galleryId)
        ),
        {}
      )
    );
  }

  public async onAllUpdatesDone(): Promise<void> {
    if (!this.ongoingUpdates.size) return;
    return new Promise(resolve => {
      const done$ = new Subject<void>();
      this.onGalleryUpdated$.pipe(takeUntil(done$)).subscribe(() => {
        if (!this.ongoingUpdates.size) {
          done$.next();
          done$.complete();
          resolve();
        }
      });
    });
  }

  private async updateSubGalleries(galleryId: string, params: UpdateGalleryBodyDto): Promise<void> {
    const subParams: UpdateGalleryBodyDto = {
      password: params.password,
      password_enabled: params.password_enabled,
    };

    if (!Object.values(subParams).filter(v => v !== undefined).length) return;
    const subGalleries = await firstValueFrom(this.getSubGalleries(galleryId));

    await Promise.all(
      subGalleries.map(gallery => {
        const _subParams = {
          ...subParams,
        };
        // When a gallery is made into a sub gallery, we currenty cant update the access levels to null so we just set them to be the same as the parent gallery
        // This means that, for now, while customers cant update the access levels of sub galleries, we need to update the sub gallery access levels
        // too when we update them for the main gallery
        if (gallery.tenant_access === null && params.tenant_access) _subParams.tenant_access = null;
        if (gallery.public_access === null && params.public_access) _subParams.public_access = null;
        return this.updateGallery(gallery.id, _subParams);
      })
    );
  }

  public filterAndHandleNewGalleries(galleries: GalleryDto[]) {
    return this.galleriesCommonService.onGetNewGalleries(galleries).pipe(map(g => unique(g, 'id')));
  }
}
